/**
 * Micro Session Narratives - 30-60 second problem/what/why/how intros
 * Based on Gabi's feedback: Quick narrative for buy-in before interactivity
 */

import { Narrative } from '@/types/micro';

export const microSessionNarratives: Record<string, Narrative> = {
  // FOUNDATIONS TRACK
  'daily-pulse': {
    problem: "You're feeling overwhelmed by endless AI possibilities and don't know where to start today.",
    what: "Pick ONE doable move. Stop spinning your wheels.",
    why: "Small daily actions build lasting AI confidence faster than trying to learn everything at once.",
    how: "Chat with Lyra for 5 minutes. Clear the noise. Get one step. Done."
  },

  'focus-reset': {
    problem: "You're drowning in noise and can't focus on what actually moves your mission forward.",
    what: "Pause. Breathe. Find the next tiny move that actually matters.",
    why: "Intentional clarity beats busy work every time - especially in nonprofit leadership.",
    how: "Take 6 minutes to clear the mental clutter. Choose your next priority. Done."
  },

  // COMMUNICATION TRACK
  'prompt-sprint': {
    problem: "Your AI prompts feel vague and the output misses the mark - you're wasting time on re-prompting.",
    what: "Turn rough ideas into ready-to-use copy in one try.",
    why: "Tight prompts save hours of iteration and produce better results faster.",
    how: "Bring a rough prompt. We'll refine it in under 7 minutes. See the quality jump immediately."
  },

  'newsletter-remix': {
    problem: "You're rewriting the same update for email, social, and website - tripling your workload.",
    what: "Flip one update into multichannel copy that actually sounds human.",
    why: "Repurposing with AI lets you maintain consistent messaging without starting from scratch every time.",
    how: "Give Lyra one piece of content. We'll transform it for email, social, and web in under 7 minutes."
  },

  // OPERATIONS TRACK
  'inbox-triage': {
    problem: "Your inbox is chaos - similar asks scattered across emails, no system for consistent replies.",
    what: "Group asks. Reply with one thoughtful note. Build your template library.",
    why: "Systematic responses keep your replies steady and kind, even when you're overwhelmed.",
    how: "Categorize messages with Lyra. Build reusable response templates in under 6 minutes."
  },

  'ops-playbook-builder': {
    problem: "Critical processes live in your head—you've become the bottleneck without meaning to.",
    what: "Get a reusable Playbook Builder prompt that turns any process into a clear, followable playbook.",
    why: "Learn this method once and you'll never struggle to document a process again.",
    how: "Answer 4 quick questions, get your reusable prompt, then practice on a second process to prove mastery."
  },

  // AUTOMATION TRACK
  'automation-spotlight': {
    problem: "You're doing the same task over and over, but can't tell if it's worth automating.",
    what: "Automation Spotlight helps you spot repeat tasks that deserve a lightweight AI or Zapier handoff.",
    why: "Not everything should be automated - but the right automations save hours every week.",
    how: "Walk through a scenario with Lyra and identify one high-value automation opportunity in 7 minutes."
  },

  // LEADERSHIP TRACK
  'signal-scan': {
    problem: "AI headlines flood your feed - you can't tell what's hype vs. what actually matters to your org.",
    what: "Signal Scan gives you a quick read on whether an AI trend deserves attention right now.",
    why: "Strategic leaders filter noise ruthlessly to focus on moves that match org readiness.",
    how: "Bring one AI headline to Lyra, analyze it together, and decide if it's signal or noise in 6 minutes."
  },

  'momentum-brief': {
    problem: "Your team needs updates but you're too busy to craft a cohesive leadership snapshot.",
    what: "Momentum Brief helps you capture one strategic readout for your team in minutes, not hours.",
    why: "Clear, consistent communication keeps your team aligned without endless meetings.",
    how: "Share key updates with Lyra and generate a confident leadership snapshot in 7 minutes."
  },

  // INSIGHT TRACK
  'research-brief-lab': {
    problem: "Interview notes and research findings are scattered - you can't synthesize them into shareable insights.",
    what: "Research Brief Lab helps you tidy findings into a shareable brief that travels well.",
    why: "Organized insights amplify your impact - stakeholders need clear takeaways, not raw data.",
    how: "Upload notes to Lyra, organize interviews and tags automatically, and generate a brief in 8 minutes."
  },

  'impact-reflection': {
    problem: "You're moving fast but never pausing to notice what's actually working or why.",
    what: "Impact Reflection creates space to note what felt right and adjust your approach.",
    why: "Learning sticks when you reflect honestly - gentle iteration beats blind hustle.",
    how: "Spend 5 minutes with Lyra noticing wins, challenges, and adjustments for next time."
  },

  // CHAPTER 2 LESSONS (when converted to micro format)
  'spot-hallucinations': {
    problem: "AI writes with total confidence—even when it's making things up. One fabricated statistic can tank your credibility.",
    what: "Learn to spot hallucinations in 30 seconds—specific numbers without sources, vague attributions, and too-good examples.",
    why: "Protect your reputation and keep your organization credible with donors, funders, and the public.",
    how: "Practice spotting red flags in realistic nonprofit content, then master the verification prompt technique."
  },

  'quality-control': {
    problem: "AI output sounds generic or off-brand - you're not confident sharing it without heavy editing.",
    what: "Quality Control teaches you to spot hallucinations, verify facts, and maintain your authentic voice.",
    why: "One fabricated fact or off-brand message can damage trust you spent years building.",
    how: "Practice quality checks with Lyra using real nonprofit scenarios, and learn verification techniques in 7 minutes."
  },

  'ai-briefing': {
    problem: "AI doesn't know your organization's voice - output sounds like it came from a corporate template.",
    what: "Briefing AI teaches you to capture your role, audience, voice, and context so AI sounds like you.",
    why: "When AI knows your organizational voice, you save hours of editing and maintain authentic relationships.",
    how: "Build your first AI brief with Lyra in 7 minutes - define who you are and what you need."
  },

  'content-intelligence': {
    problem: "Impact data is buried in boring reports - donors and stakeholders glaze over instead of engaging.",
    what: "Content Intelligence teaches you to extract compelling stories from existing materials using AI.",
    why: "You already have powerful stories in your data - you just need to surface them effectively.",
    how: "Upload one report to Lyra and transform dry facts into donor-ready narratives in 7 minutes."
  },

  'strategic-direction': {
    problem: "Communications feel reactive and scattered - no clear thread connecting messages to mission goals.",
    what: "Strategic Direction helps you align all communications with your organizational objectives.",
    why: "Every message should serve your mission - strategic alignment amplifies impact and builds trust.",
    how: "Map your communication goals with Lyra and ensure every output drives toward mission in 7 minutes."
  },

  'communication-systems': {
    problem: "You're rewriting the same emails from scratch every time—that's not a writing problem, it's a systems problem.",
    what: "Build a Communication Template that captures structure, variables, and your authentic voice.",
    why: "Learn this method once and you'll never start from scratch again.",
    how: "Answer 3 quick questions, get your reusable template, then practice on a second communication type."
  },

  // CHAPTER 3 LESSONS (when converted to micro format)
  'analyze-options': {
    problem: "You have multiple program options but no systematic way to evaluate which deserves your limited resources.",
    what: "This lesson teaches you to analyze options quickly with AI-powered comparative analysis.",
    why: "Evidence-based decisions prevent costly mistakes and build stakeholder confidence.",
    how: "Practice analyzing 3 options with Lyra in 7 minutes - get clarity on which path to pursue."
  },

  'build-plans': {
    problem: "Project plans take weeks to write - quality suffers when you're under time pressure.",
    what: "Plan Building teaches you to generate investor-quality project plans in hours, not weeks.",
    why: "Professional documentation opens doors to funding and partnerships without consuming your life.",
    how: "Build one complete project plan with Lyra's guidance in 7 minutes - ready to share."
  },

  'monitor-finances': {
    problem: "Budget surprises derail programs mid-execution - you find out about problems too late to fix them.",
    what: "Financial Monitoring teaches you to set up real-time tracking that catches issues early.",
    why: "Early detection of financial problems gives you time to course-correct before crisis hits.",
    how: "Set up AI-powered budget tracking for one project with Lyra in 7 minutes."
  },

  'optimize-workflows': {
    problem: "Your team is overwhelmed but you can't see where time is actually going or what's causing bottlenecks.",
    what: "Workflow Optimization helps you map workload, spot imbalances, and protect team sustainability.",
    why: "Burnout loses you good people - smart workload analysis prevents crisis before it starts.",
    how: "Map your team's current workflow with Lyra and identify one optimization opportunity in 7 minutes."
  },

  'automated-oversight': {
    problem: "You're manually tracking project status across multiple initiatives - visibility requires constant effort.",
    what: "Project Oversight Automation teaches you to automate status updates and maintain visibility effortlessly.",
    why: "Automated oversight frees you from micromanaging while keeping you informed on what matters.",
    how: "Set up your first automated project dashboard with Lyra in 7 minutes - never lose track again."
  },

  // AI FOUNDATIONS - Topic 1: The 4 Core Principles
  'ai-foundations-be-specific': {
    problem: "Vague prompts like 'write an email about our program' produce generic, unusable AI outputs.",
    what: "This lesson teaches you to write specific, clear prompts that get exactly what you need on the first try.",
    why: "Precision in prompting saves hours of back-and-forth revision and produces better results faster.",
    how: "Practice transforming vague requests into detailed instructions with Lyra in 5 minutes."
  },

  'ai-foundations-provide-context': {
    problem: "AI doesn't know your organization, mission, or audience - so it gives generic nonprofit advice.",
    what: "This lesson shows you how to give AI the 3-4 critical facts that personalize every output.",
    why: "Context turns AI from a generic tool into your strategic thinking partner who understands your work.",
    how: "Learn to paint the picture efficiently with Lyra in 5 minutes - without dumping your entire history."
  },

  'ai-foundations-set-format': {
    problem: "AI gives you paragraphs when you needed bullet points - or vice versa - wasting revision time.",
    what: "This lesson teaches you to define structure so AI's first draft is your final draft.",
    why: "Specifying format upfront saves hours of 'actually, can you make it...' revision cycles.",
    how: "Practice format specifications with Lyra in 5 minutes - make AI nail it on the first try."
  },

  'ai-foundations-include-examples': {
    problem: "Explaining your desired style takes paragraphs - and AI still doesn't quite get it right.",
    what: "This lesson shows you how one good example replaces five paragraphs of instructions.",
    why: "Examples capture your tone, structure, and detail level faster than any description can.",
    how: "Build your example library with Lyra in 5 minutes - show AI exactly what 'good' looks like."
  },

  // Topic 2: Evaluating & Refining
  'ai-foundations-raise-framework': {
    problem: "You're not sure if AI output is good enough to use - you rely on vague gut feeling.",
    what: "The RAISE Framework gives you a systematic 30-second quality check: Relevance, Accuracy, Impact, Style, Enhancement.",
    why: "Five quick questions catch problems before they become embarrassments or damage trust.",
    how: "Practice RAISE checks with Lyra in 7 minutes until it becomes automatic habit."
  },

  // Topic 3: Understanding AI
  'ai-foundations-understand-models': {
    problem: "ChatGPT, Claude, Gemini - they all seem the same, leading to tool frustration and poor matches.",
    what: "This lesson explains which AI models excel at writing vs. analysis vs. brainstorming for nonprofits.",
    why: "Understanding model strengths helps you avoid frustration and choose the right tool for each task.",
    how: "Learn practical model selection criteria with Lyra in 6 minutes - no technical jargon."
  },

  'ai-foundations-text-image-tools': {
    problem: "You're using text AI for everything - even tasks better suited for image, audio, or specialized tools.",
    what: "This lesson teaches you when to use text AI vs. image AI vs. specialized nonprofit tools.",
    why: "Matching the right tool type to each task turns AI from confusing to clarifying.",
    how: "Practice tool selection with real nonprofit scenarios with Lyra in 6 minutes."
  },

  'ai-foundations-ai-limitations': {
    problem: "You keep asking AI for things it can't do, wasting time and getting frustrated.",
    what: "This lesson shows you what AI genuinely can't do - so you focus on what it does brilliantly.",
    why: "Understanding limitations is liberating - you stop wasting time on impossible asks.",
    how: "Identify AI-appropriate vs. human-judgment tasks with Lyra in 6 minutes."
  },

  'ai-foundations-tool-strategy': {
    problem: "Hundreds of AI tools exist - you don't know which 3-5 tools actually fit your nonprofit workflow.",
    what: "This lesson helps you build a personal toolkit matched to your specific work and workflow.",
    why: "A focused toolkit saves time - 90% coverage with 5 tools beats 50% coverage with 20 tools.",
    how: "Audit your work and select your core AI tools with Lyra in 7 minutes."
  },

  // Topic 4: AI Ethics and Responsibility
  'ai-foundations-ethical-considerations': {
    problem: "Using AI without ethical guidelines risks harming the communities you serve and compromising mission integrity.",
    what: "This lesson helps you think through ethical implications: values alignment, transparency, potential harm, governance.",
    why: "Nonprofits operate on trust - ethical AI use strengthens mission integrity instead of compromising it.",
    how: "Work through ethical decision scenarios with Lyra in 6 minutes."
  },

  'ai-foundations-data-privacy': {
    problem: "You're not sure which stakeholder information is safe to use with AI tools - privacy feels like a guessing game.",
    what: "This lesson teaches practical safeguards: what data never goes into AI, anonymization, trustworthy tools, organizational policies.",
    why: "One careless AI interaction could expose sensitive information that destroys lives and reputations.",
    how: "Learn data privacy protocols with Lyra in 6 minutes - protect everyone you serve."
  },

  'ai-foundations-bias-fairness': {
    problem: "AI learned from a biased internet - it might perpetuate exactly the inequities your nonprofit fights against.",
    what: "This lesson helps you spot bias in AI outputs and actively work against systemic problems.",
    why: "Unchecked AI bias can generate communications that alienate the very people you're trying to serve.",
    how: "Practice bias detection with Lyra in 6 minutes - ensure AI advances equity."
  },

  'ai-foundations-ethical-guidelines': {
    problem: "'Can we use AI for that?' gets answered differently by every person on your team - creating inconsistency and risk.",
    what: "This lesson helps you draft a one-page ethical AI policy with shared frameworks and clear guardrails.",
    why: "Ethical guidelines let you use AI confidently and consistently across your organization.",
    how: "Create your organization's AI ethics framework with Lyra in 7 minutes."
  },

  // Topic 5: Setting Up Your AI Toolkit
  'ai-foundations-setup-toolkit': {
    problem: "Your 'AI toolkit' is just ChatGPT in a browser tab you keep losing - nothing organized or integrated.",
    what: "This lesson walks you through setting up a practical, sustainable AI workspace you'll actually use daily.",
    why: "An organized toolkit saves time from day one - no complicated integrations or IT expertise required.",
    how: "Set up your 3-5 core AI tools with Lyra in 7 minutes - get productive immediately."
  },

  // --- NEW PEDAGOGICAL MODES ---
  'grant-negotiation': {
    problem: "You often accept the first 'no' or 'maybe' from funders because you don't want to seem ungrateful.",
    what: "A safe sandbox to practice high-stakes advocacy. No risk, just reps.",
    why: "Every dollar you leave on the table is impact your community loses - advocacy is a duty, not a conflict.",
    how: "Roleplay a funding conversation. Handle objections. Get the 'Yes' in under 7 minutes."
  },

  'voice-tune-up': {
    problem: "Your communications sound professional but generic - like they could come from any nonprofit.",
    what: "Take 'vanilla' AI drafts and inject your specific organizational soul.",
    why: "People don't give to institutions; they give to movements. Your unique voice is your strongest asset.",
    how: "Rewrite a boring draft in 'Editor' mode. Train your ear for authenticity in under 5 minutes."
  },

  'crisis-triage': {
    problem: "Everything feels urgent, so you react to the loudest noise instead of the biggest risk.",
    what: "A realistic 'crisis scenario' to test your decision logic under pressure.",
    why: "Strategic leadership isn't about doing more; it's about ruthlessly choosing what NOT to do right now.",
    how: "Rank 5 competing fires in 'Prioritizer' mode. Impact vs. Urgency. Done in 5 minutes."
  }
};

// Helper function to get narrative for a session
export const getNarrative = (sessionId: string): Narrative | undefined => {
  return microSessionNarratives[sessionId];
};

// Helper to check if a session has a narrative defined
export const hasNarrative = (sessionId: string): boolean => {
  return sessionId in microSessionNarratives;
};
